import { Stack, Typography } from "@mui/material";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { TableColumn, TableComponent } from "@/components";
import { useMediaJobSocket } from "@/pages/Private/helpers/useMediaJobSocket";
import {
	useGetMediaJobsQuery,
	useGetScenarioCostsQuery,
} from "@/pages/Private/redux/media/media.api";
import { MediaJob, MediaJobStatus } from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";
import { formatUsd } from "@/utils/formatUsd";

interface JobsAndCostsTabProps {
	courseId: number;
}

/** Amber on amber is unreadable; the running badge writes in warning-ink on the tint. */
const STATUS_CLASSES: Record<MediaJobStatus, string> = {
	[MediaJobStatus.QUEUED]: "bg-surface text-subtle",
	[MediaJobStatus.RUNNING]: "bg-status-running-light text-warning-ink",
	[MediaJobStatus.SUCCEEDED]: "bg-success-light text-text",
	[MediaJobStatus.FAILED]: "bg-error-light text-text",
	[MediaJobStatus.CANCELLED]: "bg-surface text-subtle",
};

/**
 * Every AI job this scenario has run, and what it cost. The footer total comes from the
 * cost ledger, so it includes phase TTS as well as generated images.
 */
export const JobsAndCostsTab: FunctionComponent<JobsAndCostsTabProps> = ({ courseId }) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`courses.jobsCosts.${key}`, options ?? {}),
		[t]
	);

	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(20);

	const {
		data: jobs,
		isLoading,
		refetch: refetchJobs,
	} = useGetMediaJobsQuery({ courseId, page: page + 1, limit }, { refetchOnFocus: true });
	const { data: costs, refetch: refetchCosts } = useGetScenarioCostsQuery(courseId, {
		refetchOnFocus: true,
	});

	useMediaJobSocket({
		courseId,
		onJobUpdated: () => refetchJobs(),
		onJobSucceeded: () => {
			refetchJobs();
			refetchCosts();
		},
		onJobFailed: () => refetchJobs(),
	});

	const columns: TableColumn<MediaJob>[] = useMemo(
		() => [
			{
				label: ts("columns.job"),
				minWidth: 90,
				format: (row) => (
					<Typography className="text-ink" variant="subtitle2">
						#{row.id}
					</Typography>
				),
			},
			{
				label: ts("columns.type"),
				minWidth: 130,
				format: (row) => ts(`type.${row.type}`),
			},
			{
				label: ts("columns.details"),
				minWidth: 220,
				format: (row) => (
					<Stack direction="column">
						<Typography className="text-text" fontWeight="400" variant="body2">
							{row.phaseDataId
								? ts("phaseDetail", {
										phase: row.phaseDataId,
										variants: row.variantCount,
										model: row.model?.displayName ?? "—",
									})
								: ts("scenarioDetail", {
										variants: row.variantCount,
										model: row.model?.displayName ?? "—",
									})}
						</Typography>
						{row.prompt ? (
							<Typography noWrap className="text-subtle" fontWeight="400" variant="body2">
								{row.prompt}
							</Typography>
						) : null}
					</Stack>
				),
			},
			{
				label: ts("columns.formats"),
				minWidth: 120,
				format: (row) => row.formats.map((format) => format.label).join(", ") || "—",
			},
			{
				label: ts("columns.status"),
				minWidth: 160,
				format: (row) => (
					<Stack direction="column" spacing={0.5}>
						<span
							className={classNames(
								"w-fit rounded-full px-2.5 py-1 text-xs font-semibold",
								STATUS_CLASSES[row.status]
							)}
						>
							{ts(`status.${row.status}`)}
						</span>
						{row.status === MediaJobStatus.RUNNING && (
							<div className="h-1 w-24 overflow-hidden rounded-full bg-border-soft">
								<div
									className="h-full rounded-full bg-accent"
									style={{ width: `${Math.max(row.progress, 5)}%` }}
								/>
							</div>
						)}
					</Stack>
				),
			},
			{
				label: ts("columns.cost"),
				minWidth: 110,
				align: "right",
				format: (row) => (
					<Typography className="text-ink" variant="subtitle2">
						{row.actualCostUsd === null || row.actualCostUsd === undefined
							? `${ts("estimatePrefix")}${formatUsd(row.estimatedCostUsd)}`
							: formatUsd(row.actualCostUsd)}
					</Typography>
				),
			},
		],
		[ts]
	);

	return (
		<div className="space-y-4">
			<TableComponent<MediaJob>
				columns={columns}
				data={jobs?.items ?? []}
				handleChangeLimit={(event) => {
					setLimit(+event.target.value);
					setPage(0);
				}}
				handleChangePage={(_, nextPage) => setPage(nextPage)}
				isLoading={isLoading}
				itemCount={jobs?.total ?? 0}
				limit={limit}
				noDataText={ts("empty")}
				page={page}
			/>

			<div className="flex items-center justify-between rounded-card bg-surface px-4 py-3">
				<span className="text-xs font-semibold text-subtle">{ts("scenarioTotal")}</span>
				<span className="text-sm font-bold text-ink">{formatUsd(costs?.totalUsd ?? 0)}</span>
			</div>

			<div className="flex flex-wrap gap-2">
				{["async", "registry", "origin", "cdn"].map((key) => (
					<span
						key={key}
						className="rounded-full bg-surface px-2.5 py-1 text-xs font-semibold text-subtle"
					>
						{ts(`infra.${key}`)}
					</span>
				))}
			</div>
		</div>
	);
};
